import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { UpdateWorkOrderForm } from "@/app/ProDrivers/WorkOrders/Components/Forms/CreateWorkOrderForm";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import {
  useGqlMutation,
  useSuspenseGqlQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import { workOrderRecordQuery } from "@/app/ProDrivers/WorkOrders/GraphQL/workOrdersQuery";
import { mapAddressToInput } from "@/app/Address/Utils/mapAddressToInput";
import { updateWorkOrderMutation } from "@/app/ProDrivers/WorkOrders/GraphQL/workOrdersMutations";
import { UpdateWorkOrderInput } from "@/gql/graphql";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import { useMemo } from "react";
import { z } from "zod";
import { toFormikValidationSchema } from "@/lib/Zod/zod-formik-adapter";
import { addressValidator } from "@/app/Address/Validators/addressValidator";

const updateWorkOrderValidator = z.object({
  departureAddress: addressValidator,
  deliveryAddress: addressValidator,
});

export function UpdateWorkOrderDialog({
  workOrderId,
}: {
  workOrderId: string;
}) {
  const { close } = useDialogState();
  const { data } = useSuspenseGqlQuery(workOrderRecordQuery, {
    id: workOrderId,
  });

  const workOrder = data.workOrder!;
  const { mutateAsync } = useGqlMutation(updateWorkOrderMutation);

  const initialNotificationSettings = useMemo(
    () =>
      workOrder.notificationSettings.map((setting) => ({
        id: setting.id,
        email: setting.email,
        type: setting.type,
      })),
    [workOrder.notificationSettings],
  );

  return (
    <BaseDialog title="Update Work Order">
      <GenericForm<UpdateWorkOrderInput>
        validationSchema={toFormikValidationSchema(updateWorkOrderValidator)}
        initialValues={{
          deliver_before: workOrder.deliver_before,
          depart_after: workOrder.depart_after,
          vehicle_type: workOrder.vehicle_type,
          description: workOrder.description,
          number_of_drivers: workOrder.number_of_drivers,
          has_return_trip: workOrder.has_return_trip,
          is_service_center: workOrder.is_service_center,
          departureAddress: {
            upsert: mapAddressToInput(workOrder.departureAddress),
          },
          deliveryAddress: {
            upsert: mapAddressToInput(workOrder.deliveryAddress),
          },
          notificationSettings: {
            upsert: initialNotificationSettings,
            delete: [],
          },
        }}
        onSubmit={async (values) => {
          await mutateAsync({
            id: workOrder.id,
            input: values,
          });
          await close();
        }}
      >
        <UpdateWorkOrderForm
          supplierId={workOrder.supplier.id}
          departureCoords={{
            lat: workOrder.departureAddress?.lat,
            lng: workOrder.departureAddress?.lng,
          }}
          deliveryCoords={{
            lat: workOrder.deliveryAddress?.lat,
            lng: workOrder.deliveryAddress?.lng,
          }}
        />

        <DialogSubmitButton text="Update Work Order" />
      </GenericForm>
    </BaseDialog>
  );
}
